import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { statementsApi, unitsApi } from "../../services/api";
import { useBuilding } from "../../context/BuildingContext";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import EmptyState from "../../components/common/EmptyState";
import SearchInput from "../../components/common/SearchInput";
import PageHeader from "../../components/common/PageHeader";
import DateRangePicker from "../../components/common/DateRangePicker";
import { formatCurrency, formatDate } from "../../utils/formatters";
import { Statement, StatementFilters } from "../../modules/statementTypes";

const StatementsList: React.FC = () => {
  const [statements, setStatements] = useState<Statement[]>([]);
  const [filteredStatements, setFilteredStatements] = useState<Statement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<StatementFilters>({
    search: "",
    status: "",
    startDate: null,
    endDate: null,
    unitId: "",
  });
  const [units, setUnits] = useState<{ id: string; name: string }[]>([]);

  const { currentBuilding } = useBuilding();

  // Status options for filtering
  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "draft", label: "Draft" },
    { value: "issued", label: "Issued" },
    { value: "paid", label: "Paid" },
    { value: "overdue", label: "Overdue" },
    { value: "cancelled", label: "Cancelled" },
  ];

  useEffect(() => {
    const fetchStatements = async () => {
      if (!currentBuilding?.id) return;
      try {
        setLoading(true);
        const statementsData = await statementsApi.getAll(currentBuilding.id);
        const items: Statement[] = Array.isArray(statementsData)
          ? statementsData
          : [];
        // Fetch units for the filter dropdown — failures here should not break the page
        let unitItems: { id: string; name: string }[] = [];
        try {
          const unitsData = await unitsApi.getAll(currentBuilding.id);
          unitItems = Array.isArray(unitsData)
            ? unitsData.map((u: any) => ({ id: u.id, name: u.name }))
            : [];
        } catch (_) {
          // Units list is optional for the statements filter
        }
        setStatements(items);
        setFilteredStatements(items);
        setUnits(unitItems);
      } catch (err) {
        console.error("Error fetching statements:", err);
        setError("Failed to load statements. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchStatements();
  }, [currentBuilding?.id]);

  useEffect(() => {
    if (statements.length > 0) {
      let result = [...statements];

      // Apply search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        result = result.filter(
          (statement) =>
            statement.unitName.toLowerCase().includes(searchTerm) ||
            statement.residentName.toLowerCase().includes(searchTerm) ||
            statement.period.toLowerCase().includes(searchTerm),
        );
      }

      // Apply status filter
      if (filters.status) {
        result = result.filter(
          (statement) => statement.status === filters.status,
        );
      }

      // Apply unit filter
      if (filters.unitId) {
        result = result.filter(
          (statement) => statement.unitId === filters.unitId,
        );
      }

      // Apply date range filter
      if (filters.startDate) {
        result = result.filter(
          (statement) => statement.issueDate >= filters.startDate!,
        );
      }

      if (filters.endDate) {
        result = result.filter(
          (statement) => statement.issueDate <= filters.endDate!,
        );
      }

      setFilteredStatements(result);
    }
  }, [statements, filters]);

  const handleFilterChange = (name: string, value: string | null) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateRangeChange = (
    startDate: string | null,
    endDate: string | null,
  ) => {
    setFilters((prev) => ({
      ...prev,
      startDate,
      endDate,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      status: "",
      startDate: null,
      endDate: null,
      unitId: "",
    });
  };

  // Calculate summary data
  const calculateSummary = () => {
    const total = filteredStatements.length;
    const issued = filteredStatements.filter(
      (s) => s.status === "issued",
    ).length;
    const paid = filteredStatements.filter((s) => s.status === "paid").length;
    const overdue = filteredStatements.filter(
      (s) => s.status === "overdue",
    ).length;
    const totalValue = filteredStatements.reduce(
      (sum, s) => sum + s.totalAmount,
      0,
    );
    const paidValue = filteredStatements
      .filter((s) => s.status === "paid")
      .reduce((sum, s) => sum + (s.paidAmount || 0), 0);
    const outstandingValue = totalValue - paidValue;

    return {
      total,
      issued,
      paid,
      overdue,
      totalValue,
      paidValue,
      outstandingValue,
    };
  };

  const summary = calculateSummary();

  if (loading) {
    return <Spinner message="Loading statements..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  return (
    <div className="">
      <PageHeader
        title="Statements"
        description={`Manage service charge statements for ${
          currentBuilding?.name || "your building"
        }`}
        actions={
          <div className="flex space-x-2">
            <Link
              to="/dashboard/statements/new"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Create Statement
            </Link>
            <Link
              to="/dashboard/statements/batch"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Batch Create
            </Link>
          </div>
        }
      />

      {/* Filters */}
      <div className="mt-6 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        <div className="md:grid md:grid-cols-4 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Filters
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Narrow down statements by status, unit, or date range.
            </p>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-3">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div className="sm:col-span-6">
                <SearchInput
                  placeholder="Search by unit name, resident name, or period..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  onClear={() => handleFilterChange("search", "")}
                />
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-gray-700"
                >
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="unitId"
                  className="block text-sm font-medium text-gray-700"
                >
                  Unit
                </label>
                <select
                  id="unitId"
                  name="unitId"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  value={filters.unitId}
                  onChange={(e) => handleFilterChange("unitId", e.target.value)}
                >
                  <option value="">All Units</option>
                  {units.map((unit) => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-6">
                <DateRangePicker
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  onDateRangeChange={handleDateRangeChange}
                  startDateLabel="Issue Date From"
                  endDateLabel="Issue Date To"
                />
              </div>

              <div className="sm:col-span-6 flex justify-end">
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Summary
          </h3>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Total Statements
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {summary.total}
                </dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Total Amount
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {formatCurrency(summary.totalValue)}
                </dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Paid Amount
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-green-600">
                  {formatCurrency(summary.paidValue)}
                </dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Outstanding
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-red-600">
                  {formatCurrency(summary.outstandingValue)}
                </dd>
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6 flex flex-col items-center">
                <dt className="text-sm font-medium text-gray-500">
                  Status Breakdown
                </dt>
                <dd className="mt-4 flex justify-around w-full">
                  <div className="text-center">
                    <span className="block text-2xl font-semibold text-gray-900">
                      {summary.issued}
                    </span>
                    <span className="block text-sm text-gray-500">Issued</span>
                  </div>
                  <div className="text-center">
                    <span className="block text-2xl font-semibold text-green-600">
                      {summary.paid}
                    </span>
                    <span className="block text-sm text-gray-500">Paid</span>
                  </div>
                  <div className="text-center">
                    <span className="block text-2xl font-semibold text-red-600">
                      {summary.overdue}
                    </span>
                    <span className="block text-sm text-gray-500">Overdue</span>
                  </div>
                </dd>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statements List */}
      <div className="mt-8">
        {filteredStatements.length === 0 ? (
          <EmptyState
            title="No statements found"
            description={
              filters.search || filters.status || filters.unitId
                ? "No statements match your search criteria."
                : "You don't have any statements yet."
            }
            actionText={
              filters.search || filters.status || filters.unitId
                ? "Clear filters"
                : "Create your first statement"
            }
            actionHref={
              filters.search || filters.status || filters.unitId
                ? undefined
                : "/dashboard/statements/new"
            }
            onAction={
              filters.search || filters.status || filters.unitId
                ? handleClearFilters
                : undefined
            }
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            }
          />
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {filteredStatements.map((statement) => (
                <li key={statement.id}>
                  <Link
                    to={`/dashboard/statements/${statement.id}`}
                    className="block hover:bg-gray-50"
                  >
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-primary-600 truncate">
                            {statement.unitName} - {statement.period}
                          </p>
                          <div className="ml-2 flex-shrink-0 flex">
                            <p
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                              ${
                                statement.status === "paid"
                                  ? "bg-green-100 text-green-800"
                                  : statement.status === "overdue"
                                  ? "bg-red-100 text-red-800"
                                  : statement.status === "issued"
                                  ? "bg-primary-100 text-primary-800"
                                  : statement.status === "draft"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {statement.status.charAt(0).toUpperCase() +
                                statement.status.slice(1)}
                            </p>
                          </div>
                        </div>
                        <div className="ml-2 flex-shrink-0 flex">
                          <p className="text-sm font-medium text-gray-900">
                            {formatCurrency(statement.totalAmount)}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 sm:flex sm:justify-between">
                        <div className="sm:flex">
                          <p className="flex items-center text-sm text-gray-500">
                            <svg
                              className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                            </svg>
                            {statement.residentName}
                          </p>
                          {statement.paidDate && (
                            <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                              <svg
                                className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Paid on {formatDate(statement.paidDate)}
                            </p>
                          )}
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                          <svg
                            className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <p>
                            Issued:{" "}
                            <time dateTime={statement.issueDate}>
                              {formatDate(statement.issueDate)}
                            </time>
                          </p>
                          <p className="ml-4">
                            Due:{" "}
                            <time dateTime={statement.dueDate}>
                              {formatDate(statement.dueDate)}
                            </time>
                          </p>
                        </div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatementsList;
